import type { FC, NamedExoticComponent, ReactElement, ReactNode } from "react"
import { memo } from "react"

import { m, useReducedMotion } from "framer-motion"
import isNull from "lodash-es/isNull"
import isString from "lodash-es/isString"
import isUndefined from "lodash-es/isUndefined"

import {
    Button,
    EButtonVariant,
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { buildCssClassName, propsAreEqual, withPressHandler } from "@/3514/utils"
import { type IUseTranslation, type TTranslationKeys, useTranslation } from "@/hooks"
import { type TEmptyAsyncCallback } from "@/shared/types/functions"

import mutations from "./participant.onboarding.api.mutations.hooks"
import queries from "./participant.onboarding.api.queries.hooks"
import styles from "./participant.onboarding.styles"
import uiConfig from "./participant.onboarding.ui.config"
import uiHooks, { type TParticipantOnboardingStepsState as TStepsState } from "./participant.onboarding.ui.hooks"
import type { TParticipantOnboardingPageProps as TPageExternalProps } from "./participant.onboarding.ui.types"

const { mutationsMap } = mutations
const { useMutationOptions, useSteps, useSubmitErrorHint } = uiHooks
const { routeTitleHintsMap, routeTitlesMap } = uiConfig
const { elements: classes } = styles

type TContainerProps = { children(props: TPageExternalProps): ReactNode }
const Container: FC<TContainerProps> = ({ children }: TContainerProps): ReactElement => {
    const { currentStepByProgress: currentStep, isCurrentStepLast }: TStepsState = useSteps()

    return (
        <div className={classes.container({ step: currentStep?.name })}>
            {children({
                mutationHooks: mutationsMap[currentStep?.name],
                nextMutationOptionsHook: useMutationOptions,
                queryHooks: queries.steps,
                isLastStep: isCurrentStepLast,
                getTitle: routeTitlesMap[currentStep?.name],
                subTitle: routeTitleHintsMap[currentStep?.name]
            })}
        </div>
    )
}

type THeadingProps = {
    titleText: keyof TTranslationKeys
    subTitleText: keyof TTranslationKeys | [keyof TTranslationKeys, object] | null
    subTitleClasses?: string
    withNoAnimation?: boolean
}
const Heading: FC<THeadingProps> = ({
    titleText,
    subTitleText,
    subTitleClasses = undefined,
    withNoAnimation = false
}: THeadingProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const isMotionReduced: boolean = useReducedMotion()

    /* eslint-disable-next-line */
    function Wrapper({ children }: { children: ReactNode }): ReactElement {
        return withNoAnimation ? (
            <div className={classes.titleWrapper()}>{children}</div>
        ) : (
            <m.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{
                    duration: isMotionReduced ? 0 : 0.5,
                    type: "tween",
                    ease: "easeInOut"
                }}
                className={classes.titleWrapper()}
            >
                {children}
            </m.div>
        )
    }

    return (
        <Wrapper>
            <Typography
                text={t(titleText)}
                alignment={ETypographyAlignment.Center}
                family={ETypographyFamily.DmSerif}
                weight={ETypographyWeight.Medium}
                tag={ETypographyTag.Heading1}
                color={ETypographyColor.Black}
                size={breakpoints.xs || breakpoints.sm ? ETypographySize.Heading2 : ETypographySize.Heading1}
            />

            {isNull(subTitleText) ? null : (
                <div className={subTitleClasses}>
                    <Typography
                        text={isString(subTitleText) ? t(subTitleText) : t(subTitleText[0], { ...subTitleText[1] })}
                        alignment={ETypographyAlignment.Center}
                        family={ETypographyFamily.Nunito}
                        weight={ETypographyWeight.Default}
                        tag={ETypographyTag.Paragraph}
                        color={ETypographyColor.Dark}
                        size={ETypographySize.Small}
                    />
                </div>
            )}
        </Wrapper>
    )
}
const MemoizedHeading: NamedExoticComponent<THeadingProps> = memo<THeadingProps>(Heading, propsAreEqual)

type TBottomContainerProps = {
    containerClasses?: string
    isBusy: boolean
    isDisabled?: boolean
    isError: boolean
    isLastStep: boolean
    action?: TEmptyAsyncCallback
}
const BottomContainer: FC<TBottomContainerProps> = ({
    containerClasses = undefined,
    isBusy,
    isDisabled = false,
    isError,
    isLastStep = false,
    action = undefined
}: TBottomContainerProps): ReactElement => {
    const RenderedErrorHint: ReactElement = useSubmitErrorHint({ isVisible: isError })

    const { t }: IUseTranslation = useTranslation()

    const isMotionReduced: boolean = useReducedMotion()

    return (
        <m.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: isMotionReduced ? 0 : 0.5, delay: 0.15, type: "tween", ease: "easeInOut" }}
            className={buildCssClassName(classes.buttonContainer(), { [containerClasses]: containerClasses })}
        >
            {RenderedErrorHint}

            <Button
                isCentered
                type="submit"
                minWidth="85px"
                isDisabled={isDisabled || isError}
                isBusy={isBusy}
                variant={isLastStep ? EButtonVariant.Outlined : EButtonVariant.Solid}
                {...(!isUndefined(action) && {
                    onClick: withPressHandler(action),
                    onKeyDown: withPressHandler(action)
                })}
            >
                {isLastStep ? t("participantSide.onboarding.lastStepSubmitButton") : t("common.next")}
            </Button>
        </m.div>
    )
}

export default { Container, Heading: MemoizedHeading, BottomContainer }
